import { Injectable } from '@angular/core';

import { BetBuilderProvider } from '@cds/betting-offer/add-ons';
import { EventDetailsTags, EventModel, EventSitemapSet, SetTab } from '@frontend/sports/betting-offer/feature/model';
import { AngstromTagIdValues } from '@frontend/sports/betting-offer/feature/types';
import {
    BetBuilderConfig,
    EventDetailsConfig,
    MarketGroupingConfig,
    PriceBoostConfig,
    Sitecore,
    SitecoreEventDetailsConfig,
} from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { LocalStoreService } from '@frontend/vanilla/core';
import { isEmpty } from 'lodash-es';

import { BetBuilderConfigService } from '../../betbuilder/services/betbuilder-config.service';
import { EventDetailsService } from '../../event-details-common/event-details.service';
import { EventMarketsTabBarItem } from '../../event-details-common/header/tab-group/event-header-tab-bar.models';
import { EventDetailsSitemapModel } from '../../event-details-sitemap/event-details-sitemap.models';
import { MyBetsSummaryService } from '../../my-bets-base/my-bets-summary.service';
import { MarketGroupOrderService } from '../market-group-order/market-group-order.service';

interface EventOptions {
    canAddMybets: boolean;
    canAddBetBuilder: boolean;
    customizePrecreatedTab?: boolean;
    currentSetId?: string | number;
    desiredSetId?: string | number;
    icon?: string;
    currentSubSetId?: string;
}

enum SitecoreOrderMapping {
    AllMarkets = 'AllMarkets',
    Ogp = 'OGP',
    MyBets = 'MyBets',
    MyMarkets = 'MyMarket',
    Other = 'Other',
}

@Injectable({ providedIn: 'root' })
export class HeaderService {
    private readonly marketGroupsWithNewBadge: NumberDictionary<string[]> = {};
    private readonly defaultItemIds: (SetTab | string)[] = [SetTab.All, SetTab.BetBuilder, SetTab.MyBets];

    constructor(
        private sitecore: Sitecore,
        private sitecoreEventDetails: SitecoreEventDetailsConfig,
        private betBuilderConfig: BetBuilderConfig,
        private marketGroupingConfig: MarketGroupingConfig,
        private mybetsSummary: MyBetsSummaryService,
        protected localStorage: LocalStoreService,
        private eventDetailsService: EventDetailsService,
        private marketGroupOrderService: MarketGroupOrderService,
        private betBuilderConfigService: BetBuilderConfigService,
        private eventDetailsConfig: EventDetailsConfig,
        private priceBoostConfig: PriceBoostConfig,
    ) {
        this.marketGroupingConfig.marketGroupsWithNewBadge?.forEach((x) => {
            this.marketGroupsWithNewBadge[x.sportId] = x.marketGroupIds;
        });
    }

    getSetTabs<T extends EventMarketsTabBarItem>(
        event: EventModel,
        options: EventOptions,
        tagsFromQueryString?: string[],
        buildDefaultTabs: boolean = true,
    ): T[] {
        if (!event || !event.optionSets || (this.eventDetailsConfig.enableSitemapNavigation && !event.sitemapSets)) {
            return [];
        }
        let tabs: any[];
        if (this.eventDetailsConfig.enableSitemapNavigation) {
            tabs = this.getEventDetailsSitemap(event, tagsFromQueryString);
        } else {
            tabs = this.getOptionsSetTabs(event, options);
        }

        if (buildDefaultTabs) {
            tabs = this.tryAddDefaultTabs(tabs, event, options);
        }

        if (options.customizePrecreatedTab) {
            this.addPrecreatedTabStyles(event, tabs);
        }

        // it can be 0
        if (options.desiredSetId !== undefined) {
            const desiredSetId = options.desiredSetId === SetTab.Sgp ? tabs.find((x) => x.isSgp)?.id : options.desiredSetId;
            if (tabs.some((t) => t.id.toString() === desiredSetId?.toString())) {
                tabs.forEach((t) => (t.active = t.id.toString() === desiredSetId?.toString()));
            } else if (tabs.some((t) => t.showSeparator)) {
                tabs.forEach((t) => (t.active = !!t.showSeparator));
            }
        } else if (options.currentSetId !== undefined) {
            tabs.forEach((t) => (t.active = t.id.toString() === options.currentSetId?.toString()));
        }

        if (this.eventDetailsConfig.enableSitemapNavigation) {
            const isOnLowerLevel = tagsFromQueryString && tagsFromQueryString?.length >= 1 && !!tabs.some((t) => t.showSeparator);
            const competitionId = event.league.realCompetitionId ?? event.league.parentLeagueId ?? event.league.id;

            tabs = this.applySitemapSitecoreTabOrder(tabs, event.sport.id.toString(), !!isOnLowerLevel, competitionId);

            if (!isOnLowerLevel) {
                const firstNonDefaultIndex = tabs?.findIndex((i) => !this.defaultItemIds.includes(i.id));
                tabs[firstNonDefaultIndex].defaultPill = true;
                tabs[firstNonDefaultIndex].hasChildren = false;

                if (!tabs.some((t) => t.active)) {
                    tabs.forEach((t) => (t.active = !!t.defaultPill));
                }
            } else if (tabs.some((t) => t.showSeparator && t.id.toString() === AngstromTagIdValues.Sgp)) {
                const defaultLandingPill = tabs.find((t) => t.id.toString() === AngstromTagIdValues.Popular);

                if (defaultLandingPill) {
                    defaultLandingPill.defaultPill = true;
                }
            }
        }

        return tabs;
    }

    private addPrecreatedTabStyles<T extends EventMarketsTabBarItem>(event: EventModel, tabs: T[]) {
        const precreatedBABTab = <T>tabs?.find((tab) => tab.id === event.optionSets.find((set) => set.isPrecreated)?.id);
        if (precreatedBABTab) {
            precreatedBABTab.overrideStyles = {
                inactiveStyles: {
                    color: this.sitecoreEventDetails.eventDetails.PreCreatedBABTabTextColor,
                    borderColor: this.sitecoreEventDetails.eventDetails.PreCreatedBABTabBackgroundColor,
                    backgroundColor: this.sitecoreEventDetails.eventDetails.PreCreatedBABTabBackgroundColor,
                },
            };
        }
    }

    private applySitemapSitecoreTabOrder(
        tabs: EventMarketsTabBarItem[],
        sportId: string,
        isOnLowerLevel: boolean,
        competitionId: number,
    ): EventMarketsTabBarItem[] {
        let order: EventDetailsSitemapModel[] = [];
        let sportOrder = this.sitecoreEventDetails.sitemapOrder?.find((sport) => sport.key === `${sportId}`);

        if (!sportOrder?.children?.length) {
            return tabs;
        }

        sportOrder = sportOrder.children.find((league) => league.children?.length && league.key === `${competitionId}`) ?? sportOrder;

        if (!sportOrder.children?.length) {
            return tabs;
        }

        const separatorIndex = tabs.findIndex((tab) => tab.showSeparator);

        const tags = isOnLowerLevel ? tabs[separatorIndex].queryString?.split(':') : [];

        order = tags && tags.length ? this.getChildrenOfLastTag(tags, sportOrder.children) : sportOrder.children;

        if (isEmpty(order) || tabs.length <= 2) {
            return tabs;
        }

        let tabsToOrder: EventMarketsTabBarItem[] = [];

        if (separatorIndex !== -1) {
            tabsToOrder = tabs.slice(separatorIndex + 1);
            tabs = tabs.slice(0, separatorIndex + 1);
        } else {
            tabsToOrder = tabs;
            tabs = [];
        }

        tabsToOrder.sort((a, b) => {
            const aId = this.getTagIdValue(a.id);
            const bId = this.getTagIdValue(b.id);
            const indexA = order.findIndex((o) => o.key.toLowerCase() === aId.toLowerCase());
            const indexB = order.findIndex((o) => o.key.toLowerCase() === bId.toLowerCase());

            return indexA !== -1 && indexB !== -1 ? indexA - indexB : indexB - indexA;
        });

        return [...tabs, ...tabsToOrder];
    }

    private getTagIdValue(tab: string | number): string {
        switch (tab) {
            case SetTab.All:
                return SitecoreOrderMapping.AllMarkets;
            case SetTab.BetBuilder:
                return SitecoreOrderMapping.Ogp;
            case SetTab.MyBets:
                return SitecoreOrderMapping.MyBets;
            case SetTab.MyMarkets:
                return SitecoreOrderMapping.MyMarkets;
            case SetTab.Other:
                return SitecoreOrderMapping.Other;
            default:
                return tab.toString();
        }
    }

    private getChildrenOfLastTag(tags: string[], order: EventDetailsSitemapModel[]): EventDetailsSitemapModel[] {
        if (tags.length === 0) {
            return order;
        }

        const lastKey = tags.shift();
        const matchingChild = order?.find((child) => child.key.toLowerCase() === lastKey?.toLowerCase());

        return matchingChild && matchingChild?.children ? this.getChildrenOfLastTag(tags, matchingChild.children) : [];
    }

    private getOptionsSetTabs(event: EventModel, options: EventOptions): EventMarketsTabBarItem[] {
        let tabs: EventMarketsTabBarItem[] = event.optionSets.map((optionSet, index) => ({
            id: optionSet.id,
            title: optionSet.name,
            active: index === 0,
            marketTabOrder: optionSet.id,
            hasNewBadge: this.marketGroupHasNewBadge(event.sport.id, optionSet.storageId),
            subNavigationTabs: optionSet.tags?.map((t) => {
                return {
                    id: t.key,
                    name: t.value,
                    active: t.key === options.currentSubSetId,
                };
            }),
            isSgp: optionSet.marketGroupTag?.key === AngstromTagIdValues.Sgp,
            showSignPost: this.showSignPostForPriceBoost(optionSet.marketGroupTag?.key),

            count: this.mapCountForPriceBoost(optionSet.marketGroupTag?.key, event.live, event.priceBoostCount ?? 0),
        }));
        tabs = this.marketGroupOrderService.getSortedMarketTabs(event, tabs);

        return tabs;
    }

    private tryAddDefaultTabs<T extends EventMarketsTabBarItem>(tabs: T[], event: EventModel, options: EventOptions): T[] {
        const betBuilderTabPosition = 1;
        if (
            event.betBuilderInfo?.betBuilderProvider === BetBuilderProvider.Default &&
            options.canAddBetBuilder &&
            !event.live &&
            event.getBetBuilderConsumerFixtureId() &&
            this.betBuilderConfig.isBetBuilderEnabledForSport?.includes(event.sport.id) &&
            (!this.betBuilderConfigService.useBetBuilderInEntainUI(event.sport.id) || !!event.sportcastId)
        ) {
            tabs.splice(betBuilderTabPosition, 0, {
                id: SetTab.BetBuilder,
                title: this.sitecoreEventDetails.eventDetails.BetBuilderTabName,
                hasNewBadge: this.betBuilderConfig.isBetBuilderNewBadgeEnabledForSport?.includes(event.sport.id),
                classes: this.betBuilderConfigService.useBetBuilderInEntainUI(event.sport.id) ? 'ogp-bet-builder-tab' : 'bet-builder-tab-style',
                icon: options.icon,
            } as T);
        }

        const optionGroups = this.eventDetailsService.getOptionGroupForMyMarkets(event);
        if (optionGroups?.length) {
            if (tabs.length && tabs[betBuilderTabPosition].id === SetTab.BetBuilder) {
                tabs.splice(betBuilderTabPosition + 1, 0, { id: SetTab.MyMarkets, title: this.sitecoreEventDetails.eventDetails.MyMarkets } as T);
            } else {
                tabs.splice(betBuilderTabPosition, 0, { id: SetTab.MyMarkets, title: this.sitecoreEventDetails.eventDetails.MyMarkets } as T);
            }
        }

        if (tabs.length > 1) {
            tabs.unshift({ id: SetTab.All, title: this.sitecoreEventDetails.eventDetails.AllMarketsTemplate } as T);
        }

        const myBetsTab = this.buildMybetsTab(event);
        if (options.canAddMybets && myBetsTab.count) {
            tabs.unshift(myBetsTab as T);
        }

        return tabs;
    }

    private getEventDetailsSitemap(event: EventModel, queryStringTags?: string[]): EventMarketsTabBarItem[] {
        if (!queryStringTags || !queryStringTags.length) {
            return this.buildMarketTabBarItems(event.sitemapSets!, event.live, event.priceBoostCount);
        }

        let tags = event.sitemapSets!;
        const parents: EventMarketsTabBarItem[] = [];
        let queryString = '';

        for (let i = 0; i < queryStringTags.length; i++) {
            const tag = queryStringTags[i];
            const currentTag = tags.find((set) => set.id.toString() === tag);

            if (!currentTag || currentTag.children!.length === 0) {
                break;
            }

            parents.push(this.mapMarketTabBarItem(currentTag, event.live, queryString, event.priceBoostCount));
            queryString += i === 0 ? tag : `:${tag}`;
            tags = currentTag.children!;
        }

        if (parents.length > 0) {
            parents[parents.length - 1].showSeparator = true;
        }

        return [...parents, ...this.buildMarketTabBarItems(tags, event.live, event.priceBoostCount, queryString)];
    }

    private buildMarketTabBarItems(
        sets: EventSitemapSet[],
        isLive: boolean,
        priceBoostCount?: number,
        parentQueryString?: string,
    ): EventMarketsTabBarItem[] {
        return sets.map((set) => {
            const tabBar = this.mapMarketTabBarItem(set, isLive, parentQueryString, priceBoostCount);

            return tabBar;
        });
    }

    private mapMarketTabBarItem(set: EventSitemapSet, isLive: boolean, parentQueryString?: string, priceBoostCount?: number): EventMarketsTabBarItem {
        return {
            id: set.id,
            title: set.name,
            marketGroups: set.groups,
            queryString: parentQueryString ? `${parentQueryString}:${set.id}` : set.id.toString(),
            hasChildren: set.children && set.children.length > 0,
            isSgp: set.id === AngstromTagIdValues.Sgp || parentQueryString === AngstromTagIdValues.Sgp,
            showSignPost: this.showSignPostForPriceBoost(set.id),
            count: this.mapCountForPriceBoost(set.id, isLive, priceBoostCount ?? 0),
        };
    }

    private mapCountForPriceBoost(key: string | number | undefined, isLive: boolean, priceBoostCount: number): number {
        if (this.showSignPostForPriceBoost(key) && priceBoostCount > 0) {
            if (isLive) {
                return this.priceBoostConfig.showCountForLive ? priceBoostCount : 0;
            } else {
                return priceBoostCount;
            }
        }

        return 0;
    }

    private showSignPostForPriceBoost(key: string | number | undefined) {
        return this.priceBoostConfig.isEnabled && key === EventDetailsTags.PriceBoost;
    }

    private buildMybetsTab(event: EventModel): EventMarketsTabBarItem {
        const betsCount = this.mybetsSummary.getOpenBetsCount(event);
        const myBetsTitle = this.sitecore.globalMessages.MyBetsTitle;

        const tab = {
            id: SetTab.MyBets,
            title: this.eventDetailsConfig.enableSitemapNavigation ? `${myBetsTitle} (${betsCount})` : myBetsTitle,
            count: betsCount,
        };

        return tab;
    }

    marketGroupHasNewBadge(sportId: number, marketGroupId: string) {
        return !!this.marketGroupsWithNewBadge[sportId]?.find((mg) => mg === marketGroupId);
    }
}
